/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import axios, { AxiosInstance, AxiosError, isCancel } from 'axios'
import { DateTime } from 'luxon'

import { resolveApiPath } from '@/providers/apiRoutes'
import { reportApiError } from '@/providers/errorReporter'
import { saveToken } from '@/services/authService'
import type { ApiResponse, ApiService, RequestConfig, SimpleApiResponse } from '@/types/dashboard-types'
import formatException from '@/utils/formatException'
import logger from '@/utils/logger'

// Module-level callback for 401 handling (registered by AuthProvider)
let onNeedsHardLogin: (() => void) | null = null
export const registerNeedsHardLoginCallback = (cb: () => void) => {
  onNeedsHardLogin = cb
}
export const unregisterNeedsHardLoginCallback = () => {
  onNeedsHardLogin = null
}

const log = logger({ category: 'api' })

const iso8601Regex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/

// Create a base axios instance
const createApiClient = (): AxiosInstance => {
  const baseURL = `${window.VITE_MEGALODON_SERVER_URL || import.meta.env.VITE_MEGALODON_SERVER_URL || 'http://localhost:3000'}`
  log.info('create api client, baseURL: %o', baseURL)
  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  })

  client.interceptors.request.use(async (config) => {
    if (config.headers.Authorization) {
      return config
    }
    const token = localStorage.getItem('MEGA_API_TOKEN')
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    } else if (!/auth\/token/.test(config.url || '')) {
      onNeedsHardLogin?.()
      throw new Error('No token')
    }
    return config
  })

  // Pick up server-refreshed tokens from the X-Refreshed-Token header
  client.interceptors.response.use((response) => {
    const refreshedToken = response.headers['x-refreshed-token']
    if (refreshedToken) {
      localStorage.setItem('MEGA_API_TOKEN', refreshedToken)
      saveToken(refreshedToken)
    }
    return response
  })

  // 401 means the token is genuinely invalid (expired 24h token or revoked)
  client.interceptors.response.use(undefined, async (error: AxiosError) => {
    if (error.response?.status === 401 && !/auth\/token/.test(error.config?.url || '')) {
      onNeedsHardLogin?.()
    }
    // Every verb fails through here, so this is where server errors and lost requests are
    // reported for the error digest (errorReporter.ts filters and throttles).
    reportApiError(client, error)
    return Promise.reject(error)
  })

  return client
}

// Create the API client instance but need to wait until config is loaded
// so don't do it in module scope
let client: AxiosInstance
const apiClient = () => {
  if (client) {
    return client
  }
  client = createApiClient()
  return client
}

// Every verb funnels through here, so this is where the Fresh → Timescale
// backend split is applied. See apiRoutes.ts for why the client asks for
// /fresh explicitly rather than relying on the backend's aliases.
const withOrigin = (path: string, origin: string) => {
  const resolved = resolveApiPath(path)
  return resolved.includes('?') ? `${resolved}&origin=${origin}` : `${resolved}?origin=${origin}`
}

// It's nice to define types using Date but they come over the wire
// as strings, so this finds them and converts them back to Dates,
// allowing you to treat them as Date on both client and server
const convertDates = <T>(data: T): T => {
  if (Array.isArray(data)) {
    return data.map(convertDates) as T
  }
  if (data && typeof data === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(data)) {
      if (typeof value === 'string' && iso8601Regex.test(value) && DateTime.fromISO(value).isValid) {
        result[key] = DateTime.fromISO(value).toJSDate()
      } else {
        result[key] = convertDates(value)
      }
    }
    return result as T
  }
  return data as T
}

const sessionCache = new Map<string, { data: unknown }>()

const CACHE_PREFIX = 'api_cache:'

export const clearAllCaches = () => {
  sessionCache.clear()
  const keysToRemove = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (key?.startsWith(CACHE_PREFIX)) keysToRemove.push(key)
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key))
}

const parseCacheDuration = (expiresIn: string): number => {
  const match = expiresIn.match(/^(\d+)([smh])$/)
  if (!match) return 0
  const [, value, unit] = match
  const multipliers: Record<string, number> = { s: 1000, m: 60_000, h: 3_600_000 }
  return Number(value) * multipliers[unit]
}

const cacheKey = (path: string) => path.replace(/[?&]origin=[^&]*/, '')

const getCached = <T>(path: string, expiresIn: string): ApiResponse<T> | null => {
  const key = cacheKey(path)
  if (expiresIn === 'session') {
    const entry = sessionCache.get(key)
    return entry ? (entry.data as ApiResponse<T>) : null
  }
  const raw = localStorage.getItem(CACHE_PREFIX + key)
  if (!raw) return null
  const { data, expiresAt } = JSON.parse(raw)
  if (Date.now() > expiresAt) {
    localStorage.removeItem(CACHE_PREFIX + key)
    return null
  }
  return convertDates(data) as ApiResponse<T>
}

// Remove any expired api_cache entries to free up space. Returns true if it
// removed anything worth retrying for.
const evictExpiredCache = (): boolean => {
  const now = Date.now()
  const keysToRemove: string[] = []
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (!key?.startsWith(CACHE_PREFIX)) continue
    try {
      const raw = localStorage.getItem(key)
      if (raw && now > JSON.parse(raw).expiresAt) keysToRemove.push(key)
    } catch {
      // Corrupt/unparseable entry — drop it.
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key))
  return keysToRemove.length > 0
}

const setCache = <T>(path: string, expiresIn: string, data: ApiResponse<T>) => {
  const key = cacheKey(path)
  if (expiresIn === 'session') {
    sessionCache.set(key, { data })
    return
  }
  const ms = parseCacheDuration(expiresIn)
  if (!ms) return
  const value = JSON.stringify({ data, expiresAt: Date.now() + ms })
  try {
    localStorage.setItem(CACHE_PREFIX + key, value)
  } catch (ex: unknown) {
    // Out of localStorage quota (large payloads like full geofence lists).
    // Caching is a best-effort optimization, so never let a failure here
    // break the request itself. Try to make room by dropping expired
    // entries, then give up quietly if it still won't fit.
    log.debug('Cache write for %s failed, retrying after eviction: %o', key, ex)
    if (evictExpiredCache()) {
      try {
        localStorage.setItem(CACHE_PREFIX + key, value)
        return
      } catch {
        // Fall through to the give-up warning below.
      }
    }
    // Eviction freed nothing usable, or the entry is simply too large to
    // ever fit — stop caching this entry. This is the only outcome worth
    // surfacing, since it means the cache is genuinely saturated.
    log.warn('Skipping cache for %s: localStorage quota exceeded', key)
  }
}

/**
 * Turn a failed request into an ApiResponse, preferring the server's own
 * message and status.
 *
 * Axios only offers "Request failed with status code 400", which tells the user
 * nothing about what was actually wrong — our endpoints send a real reason in
 * the body, and that is what belongs on screen.
 */
const toErrorResponse = <T>(ex: unknown): ApiResponse<T> => {
  const axiosError = ex as AxiosError
  const body = axiosError.response?.data as ApiResponse<T> | undefined
  // No response at all (network down, timeout): axios's "Network Error" means nothing to a user.
  const unreachable = !axiosError.response && Boolean(axiosError.request) && !isCancel(ex)
  return {
    error: true,
    code: axiosError.response?.status || 500,
    message: unreachable
      ? 'Could not reach the server. Check your connection and try again.'
      : body?.message || formatException(ex),
  }
}

const get = async <T>({ path, origin, expiresIn }: RequestConfig): Promise<ApiResponse<T>> => {
  if (expiresIn) {
    const cached = getCached<T>(path, expiresIn)
    if (cached) return cached
  }
  try {
    log.debug('Fetching %s', path)
    const response = await apiClient().get(withOrigin(path, origin))
    const data: T = convertDates(response.data) as T
    const result: ApiResponse<T> = { ...data, code: response.status }
    if (expiresIn) setCache(path, expiresIn, result)
    return result
  } catch (ex: unknown) {
    log.error('path: %s, error: %o', path, ex)
    return toErrorResponse<T>(ex)
  }
}

const post = async <T, B>({ path, body, origin }: RequestConfig & { body?: B }): Promise<ApiResponse<T>> => {
  try {
    log.debug('Posting %s', path)
    const response = await apiClient().post(withOrigin(path, origin), body ?? {})
    const data: T = convertDates(response.data) as T
    return { ...data, code: response.status }
  } catch (ex: unknown) {
    log.error(ex)
    return toErrorResponse<T>(ex)
  }
}

const remove = async <T>({ path }: RequestConfig): Promise<ApiResponse<T>> => {
  try {
    log.debug('Deleting %s', path)
    const response = await apiClient().delete(withOrigin(path, origin))
    const data: T = convertDates(response.data) as T
    return { ...data, code: response.status }
  } catch (ex: unknown) {
    log.error(ex)
    return toErrorResponse<T>(ex)
  }
}

const put = async <T, B>({ path, body, origin }: RequestConfig & { body?: B }): Promise<ApiResponse<T>> => {
  try {
    log.debug('Putting %s', path)
    const response = await apiClient().put(withOrigin(path, origin), body)
    const data: T = convertDates(response.data) as T
    return { ...data, code: response.status }
  } catch (ex: unknown) {
    log.error(ex)
    return toErrorResponse<T>(ex)
  }
}

const simpleGet = async <T>({ path, origin }: RequestConfig): Promise<SimpleApiResponse<T>> => {
  log.debug('Fetching %s', path)
  const response = await apiClient().get(withOrigin(path, origin))
  return response.data as T
}

const simplePost = async <T, B>({
  path,
  body,
  origin,
  token,
}: RequestConfig & { body?: B; token?: string }): Promise<SimpleApiResponse<T>> => {
  log.debug('Posting %s', path)
  const headers: Record<string, string> = {}
  if (token) {
    if (typeof token !== 'string') {
      throw new Error('Token must be a string. Got: ' + Object.prototype.toString.call(token))
    }
    headers.Authorization = `Bearer ${token}`
  }
  const response = await apiClient().post(withOrigin(path, origin), body ?? {}, { headers })
  return response.data as T
}

/**
 * Post a raw body with its own Content-Type — an image upload, where the server reads
 * the bytes straight from the body. Overrides the client's JSON default for this call only.
 */
const simplePostBinary = async <T>({
  path,
  body,
  contentType,
  origin,
}: RequestConfig & { body: Blob; contentType: string }): Promise<SimpleApiResponse<T>> => {
  log.debug('Posting %s (%s)', path, contentType)
  const response = await apiClient().post(withOrigin(path, origin), body, {
    headers: { 'Content-Type': contentType },
  })
  return response.data as T
}

const simpleRemove = async <T>({ path }: RequestConfig): Promise<SimpleApiResponse<T>> => {
  log.debug('Deleting %s', path)
  const response = await apiClient().delete(withOrigin(path, origin))
  return response.data as T
}

const simplePut = async <T, B>({ path, body, origin }: RequestConfig & { body?: B }): Promise<SimpleApiResponse<T>> => {
  log.debug('Putting %s', path)
  const response = await apiClient().put(withOrigin(path, origin), body)
  return response.data as T
}

export const apiService: ApiService = {
  get,
  post,
  put,
  remove,
  simpleGet,
  simplePost,
  simplePostBinary,
  simplePut,
  simpleRemove,
}
